"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { randomId } from "@lib/randomId";
import { parseLocalizedTitles } from "@lib/localizedTitles";
import type { AgentActionStatus } from "@lib/agent/actionToken";
import type { AgentLessonContent } from "@lib/agent/lessonSnapshot";
import { applyLessonContentPatch, mergeLessonContextInput } from "@lib/agent/lessonContextState";
import type { AgentPageContext } from "@lib/agent/pageContext";
import type { ChatAboutFeedbackDetail } from "@lib/agent/chatAboutFeedback";
import type {
  AgentEvent,
  LessonContext,
  LessonContextInput,
  OverlayMessage,
  OverlayState,
} from "./types";
import { lessonTypeToPhase } from "./types";
import { useInputCapture } from "./useInputCapture";
import { useAgentStream } from "./useAgentStream";
import type {
  AgentPromptPayload,
  FireOptions,
  PromptHistoryEntry,
  PromptTrigger,
} from "./useAgentStream";
import { useEventQueue } from "./useEventQueue";
import type { DebugEvent } from "./useEventQueue";

// Re-export types that consumers import from this module
export type { AgentPromptPayload, PromptTrigger, PromptHistoryEntry };
export type { DebugEvent };

export type DebugRefs = {
  lastFiredAt: React.MutableRefObject<number | null>;
  lastActivityAt: React.MutableRefObject<number>;
  performance: React.MutableRefObject<{ correct: number; incorrect: number; streak: number }>;
  pendingDelayUntil: React.MutableRefObject<number | null>;
  events: React.MutableRefObject<DebugEvent[]>;
  lessonContext: React.MutableRefObject<LessonContextInput | null>;
  promptHistory: React.MutableRefObject<PromptHistoryEntry[]>;
  lastStreamError: React.MutableRefObject<string | null>;
};

const TUTOR_MODE = { type: "tutor" as const };

const INITIAL_OVERLAY_STATE: OverlayState = {
  mode: TUTOR_MODE,
  messages: [],
  isOpen: false,
  nudgeAt: 0,
};

function isNonTutorMode(mode: OverlayState["mode"]): boolean {
  return mode.type !== "tutor";
}

/**
 * Everything the overlay exposes. AgentOverlayProvider republishes this object
 * verbatim as its context value, so this type is the context's shape too - it
 * used to be restated there, and a capability added here simply never reached
 * the 51 consumers that read it through context.
 */
export type UseAgentOverlayReturn = {
  state: OverlayState;
  dispatchEvent: (event: AgentEvent) => void;
  sendUserMessage: (text: string) => void;
  setLessonContext: (ctx: LessonContextInput | null) => void;
  setLessonContent: (content: AgentLessonContent | null) => void;
  setPageContext: (ctx: AgentPageContext | null) => void;
  updatePageData: (patch: Record<string, unknown>) => void;
  dismiss: () => void;
  open: () => void;
  loadTutorMessages: (messages: OverlayMessage[]) => void;
  setActionStatus: (messageId: string, status: AgentActionStatus) => void;
  debugRefs: DebugRefs;
  getPromptPayload: () => AgentPromptPayload;
};

export function useAgentOverlay(): UseAgentOverlayReturn {
  const [state, setState] = useState<OverlayState>(INITIAL_OVERLAY_STATE);
  const modeRef = useRef(state.mode);

  useEffect(() => {
    modeRef.current = state.mode;
  }, [state.mode]);

  // Live mirror of the transcript so fire() can replay recent turns to the
  // tutor stream. Updated post-commit, so at fire time it holds the turns
  // before the just-sent user message.
  const messagesRef = useRef<OverlayMessage[]>(state.messages);
  useEffect(() => {
    messagesRef.current = state.messages;
  }, [state.messages]);

  const lessonContextRef = useRef<LessonContextInput | null>(null);
  const pendingContentRef = useRef<AgentLessonContent | null>(null);
  const pageContextRef = useRef<AgentPageContext | null>(null);
  const eventsRef = useRef<AgentEvent[]>([]);
  const performanceRef = useRef({ correct: 0, incorrect: 0, streak: 0 });
  const lastFiredRef = useRef<number | null>(null);
  const pendingDelayUntilRef = useRef<number | null>(null);
  const promptHistoryRef = useRef<PromptHistoryEntry[]>([]);
  const lastStreamErrorRef = useRef<string | null>(null);

  // Stable ref to break the circular dep: useEventQueue reads fireRef.current at dispatch time
  const fireRef = useRef<
    (userMessage?: string, trigger?: PromptTrigger, options?: FireOptions) => void
  >(() => {});

  const buildLessonContext = useCallback((): LessonContext | null => {
    const ctx = lessonContextRef.current;
    const lesson = ctx?.lesson;
    if (!lesson) return null;
    return {
      lesson,
      content: ctx?.content ?? null,
      lessonId: String(lesson.id),
      lessonName: parseLocalizedTitles(lesson.titles).target || lesson.name || String(lesson.id),
      phase: ctx?.phase ?? lessonTypeToPhase(lesson.lessonType),
      recentEvents: eventsRef.current.slice(-10),
      performance: { ...performanceRef.current },
      targetLocale: ctx?.targetLocale,
      nativeLocale: ctx?.nativeLocale,
    };
  }, []);

  const buildPayload = useCallback(
    () => ({
      pageContext: pageContextRef.current,
      lessonContext: buildLessonContext(),
    }),
    [buildLessonContext],
  );

  const { fire, cancelStream, resetStreamHistory, lastPromptPayloadRef } = useAgentStream({
    setState,
    buildPayload,
    lastFiredRef,
    pendingDelayUntilRef,
    lastStreamErrorRef,
    promptHistoryRef,
    messagesRef,
  });

  useEffect(() => {
    fireRef.current = fire;
  }, [fire]);

  const {
    dispatchEvent: rawDispatchEvent,
    resetEventQueue,
    debugEventsRef,
  } = useEventQueue({
    fireRef,
    lastFiredRef,
    pendingDelayUntilRef,
    lessonContextRef,
    eventsRef,
    performanceRef,
  });

  const dispatchEvent = useCallback(
    (event: AgentEvent) => {
      if (isNonTutorMode(modeRef.current)) return;
      rawDispatchEvent(event);
    },
    [rawDispatchEvent],
  );

  const resetLessonSession = useCallback(() => {
    eventsRef.current = [];
    performanceRef.current = { correct: 0, incorrect: 0, streak: 0 };
    resetEventQueue();
    resetStreamHistory();
  }, [resetEventQueue, resetStreamHistory]);

  const setLessonContext = useCallback(
    (ctx: LessonContextInput | null) => {
      if (ctx === null) {
        lessonContextRef.current = null;
        pendingContentRef.current = null;
        resetLessonSession();
        return;
      }
      const merged = mergeLessonContextInput(
        lessonContextRef.current,
        pendingContentRef.current,
        ctx,
      );
      lessonContextRef.current = merged.ctx;
      pendingContentRef.current = merged.pendingContent;
    },
    [resetLessonSession],
  );

  const setLessonContent = useCallback((content: AgentLessonContent | null) => {
    if (content === null) {
      if (lessonContextRef.current) {
        lessonContextRef.current = { ...lessonContextRef.current, content: null };
      }
      pendingContentRef.current = null;
      return;
    }
    const patched = applyLessonContentPatch(
      lessonContextRef.current,
      pendingContentRef.current,
      content,
    );
    lessonContextRef.current = patched.ctx;
    pendingContentRef.current = patched.pendingContent;
  }, []);

  const setPageContext = useCallback((ctx: AgentPageContext | null) => {
    pageContextRef.current = ctx;
  }, []);

  /** Merge client-side state (e.g. selected radar spoke) into the page data. */
  const updatePageData = useCallback((patch: Record<string, unknown>) => {
    const current = pageContextRef.current;
    if (!current) return;
    pageContextRef.current = { ...current, data: { ...current.data, ...patch } };
  }, []);

  const getPromptPayload = useCallback(
    (): AgentPromptPayload => ({
      ...buildPayload(),
      userMessage: lastPromptPayloadRef.current?.userMessage,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildPayload],
  );

  const sendUserMessage = useCallback(
    (text: string) => {
      if (isNonTutorMode(modeRef.current)) return;
      const trimmed = text.trim();
      if (!trimmed) return;
      setState((prev) => ({
        ...prev,
        isOpen: true,
        messages: [
          ...prev.messages,
          { id: randomId(), text: trimmed, isStreaming: false, role: "user" },
        ],
      }));
      fire(trimmed, "user_message");
    },
    [fire],
  );

  useEffect(() => {
    function handleChatAboutFeedback(event: Event) {
      const detail = (event as CustomEvent<ChatAboutFeedbackDetail>).detail ?? {};
      if (detail.message) sendUserMessage(detail.message);
    }

    window.addEventListener("convo:chat-about-feedback", handleChatAboutFeedback);
    return () => {
      window.removeEventListener("convo:chat-about-feedback", handleChatAboutFeedback);
    };
  }, [sendUserMessage]);

  const dismiss = useCallback(() => {
    cancelStream();
    setState((prev) => ({ ...prev, isOpen: false }));
  }, [cancelStream]);

  const open = useCallback(() => setState((prev) => ({ ...prev, isOpen: true })), []);

  const loadTutorMessages = useCallback((messages: OverlayMessage[]) => {
    setState((prev) => ({ ...prev, messages }));
  }, []);

  // CON-123: the chip used to own this, so the outcome died with the component -
  // it never reached the model or the database, and a reloaded chip offered to
  // re-run an action that had already run.
  const setActionStatus = useCallback((messageId: string, status: AgentActionStatus) => {
    setState((prev) => ({
      ...prev,
      messages: prev.messages.map((m) => (m.id === messageId ? { ...m, actionStatus: status } : m)),
    }));
  }, []);

  const handleStall = useCallback(() => {
    if (isNonTutorMode(modeRef.current)) return;
    if (!lessonContextRef.current) return;
    dispatchEvent({ type: "stall", payload: "no interaction", relativeMs: 0 });
  }, [dispatchEvent]);

  const { lastActivityRef } = useInputCapture(handleStall, true);

  // All members are stable refs - memoize the wrapper object to avoid re-creating it every render
  const debugRefs: DebugRefs = useMemo(
    () => ({
      lastFiredAt: lastFiredRef,
      lastActivityAt: lastActivityRef,
      performance: performanceRef,
      pendingDelayUntil: pendingDelayUntilRef,
      events: debugEventsRef,
      lessonContext: lessonContextRef,
      promptHistory: promptHistoryRef,
      lastStreamError: lastStreamErrorRef,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return {
    state,
    dispatchEvent,
    sendUserMessage,
    setLessonContext,
    setLessonContent,
    setPageContext,
    updatePageData,
    dismiss,
    open,
    loadTutorMessages,
    setActionStatus,
    debugRefs,
    getPromptPayload,
  };
}
